/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import Carousel from "@/components/explore-sections/shared/carousel";
import ImageComponent from "@/components/ui/image";
import BakersHero1 from "@/assets/images/bakers_hero1.webp";
import { useMediaQuery } from "react-responsive";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { getCollectionById } from "@/services/api/collections";
import UniversalButton from "@/components/common/UniversalButton";
import SectionHeading from "@/components/explore-sections/shared/heading";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { MOST_LOVED_GIFTS_COLLECTION_ID } from "@/constants/product";
import { sendGAEvent } from "@next/third-parties/google";
import { HomeSectionSkeleton } from "@/components/ui/skeleton";

interface MostLovedGifts {
  id: string | number;
  image: string;
  description: string;
  vendor: string;
  slug: string;
}

export default function MostLovedGifts() {
  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const router = useRouter();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["collection", MOST_LOVED_GIFTS_COLLECTION_ID.toString()],
    queryFn: () => getCollectionById(MOST_LOVED_GIFTS_COLLECTION_ID.toString()),
  });

  const mostLovedGifts = useMemo(
    () =>
      data?.products?.results
        ?.filter((product: any) => product.slug && product.vendor_details?.slug)
        ?.map((product: any) => ({
          id: product.id,
          image: product.product_urls?.[0] || BakersHero1,
          description: product.name || "Product",
          vendor: product.vendor_details.slug,
          slug: product.slug,
        }))
        ?.slice(0, 8) || [],
    [data?.products?.results],
  );

  let itemsPerPage = 4;
  if (isMobile) itemsPerPage = 2;

  const handleItemClick = (item: MostLovedGifts) => {
    sendGAEvent("event", "order_the_most_loved_gifts_item_clicked", {
      item_name: item.description,
      item_id: String(item.id),
      vendor_name: item.vendor,
    });
    router.push(`/${item.vendor}/product/${item.slug}`);
  };

  if (isError) {
    toast.error("Error fetching most loved gifts");
    return null;
  }

  if (isLoading) {
    return <HomeSectionSkeleton />;
  }

  if (!data || !data.slug) {
    return null;
  }

  return (
    <div className={`mt-8 px-2 lg:mt-12 lg:px-12 max-w-[1440px] mx-auto`}>
      <SectionHeading
        heading={data.name || "Order The Most-Loved Gifts"}
        subHeading="Gifts that never fail to make people happy."
        link={`/listing/${data.slug}?id=${MOST_LOVED_GIFTS_COLLECTION_ID}`}
        onClick={() =>
          sendGAEvent("event", "show_more_most_loved_gifts_clicked")
        }
      />
      <p className="font-outfit lg:hidden font-normal text-center text-sm text-v2-on-surface-variant/80 uppercase tracking-widest mt-2">
        the tooffi favourites
      </p>
      <div className="snap-x snap-mandatory w-full mt-8 overflow-x-auto lg:mt-8">
        <Carousel
          items={mostLovedGifts}
          {...(!isMobile && { itemsPerPage })}
          renderItem={(item, idx) => (
            <div
              key={idx}
              className={`group flex flex-col justify-start items-center ${
                isMobile
                  ? "flex-none w-[40%] h-[280px] me-2.5"
                  : "flex-none w-[calc((100%-72px)/4)] h-[330px]"
              } cursor-pointer`}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() =>
                router.prefetch(`/${item.vendor}/product/${item.slug}`)
              }
            >
              <div className="rounded w-full h-[220px] sm:rounded-lg sm:h-[280px] md:h-[280px] transition-transform duration-300 ease-in-out relative overflow-hidden">
                <ImageComponent
                  src={item.image}
                  alt={item.description}
                  width={400}
                  height={400}
                  sizes="(max-width: 640px) 300px, (max-width: 1024px) 200px, 360px"
                  className="w-full h-full object-cover object-center transition-transform duration-300 ease-in-out hover:scale-105 group-hover:scale-105"
                  fallbackSrc={BakersHero1}
                />
              </div>
              <h3 className="line-clamp-1 overflow-hidden text-ellipsis whitespace-normal text-center w-full font-lora text-[#303030] group-hover:text-[#735c00] mt-2 text-base font-medium leading-6 lg:text-left lg:text-xl lg:leading-7">
                {item.description}
              </h3>
            </div>
          )}
        />
      </div>
      <div className="flex justify-center items-center w-full py-4 lg:pt-0 lg:hidden">
        <UniversalButton
          text="VIEW ALL"
          classNames="lg:text-xl font-lora font-extrabold bg-[#037A6A] text-base text-[#F3F3F3] lg:px-10 lg:py-4 px-3 py-2 rounded-lg"
          onClick={() => {
            sendGAEvent("event", "show_more_most_loved_gifts_clicked");
            router.push(
              `/listing/${data.slug}?id=${MOST_LOVED_GIFTS_COLLECTION_ID}`,
            );
          }}
          variant="custom"
        />
      </div>
    </div>
  );
}
